import type { ChatInboxListRow } from '../ChatScreen/chatViewModels';

import { Platform, ScrollView } from 'react-native';

import { XStack, YStack } from 'tamagui';

import { HeadingL, Icon, LabelL, getTestProps } from '@south-street-app/atoms';

import { ConversationRow } from './ConversationRow';

type ChatInboxListProps = {
  rows: ChatInboxListRow[];
  isDesktop: boolean;
  onPressArchived: () => void;
  onPressRow: (kind: ChatInboxListRow['kind']) => void;
};

type ChannelIconProps = {
  iconName: Parameters<typeof Icon>[0]['iconName'];
  backgroundColor: string;
  iconSize?: Parameters<typeof Icon>[0]['size'];
  testID?: string;
};

const pressStyle = { opacity: 0.8 };

const ChannelIcon = ({
  iconName,
  backgroundColor,
  iconSize = '$5',
  testID,
}: ChannelIconProps) => (
  <YStack
    alignItems={'center'}
    backgroundColor={backgroundColor as '$brand500'}
    borderRadius={'$full'}
    height={'$9'}
    justifyContent={'center'}
    width={'$9'}
    {...getTestProps(testID)}
  >
    <Icon color={'$mono100'} iconName={iconName} size={iconSize} />
  </YStack>
);

const ArchivedChatsButton = ({
  isDesktop,
  onPress,
}: {
  isDesktop: boolean;
  onPress: () => void;
}) => (
  <XStack
    alignItems={'center'}
    backgroundColor={'$mono100'}
    borderRadius={isDesktop ? '$true' : 12}
    borderWidth={isDesktop ? 0 : 1}
    borderColor={isDesktop ? undefined : '$mono300'}
    minHeight={'$14'}
    justifyContent={'space-between'}
    marginBottom={isDesktop ? '$5' : '$4'}
    marginHorizontal={isDesktop ? '$5' : '$4'}
    marginTop={isDesktop ? 0 : '$4'}
    onPress={onPress}
    paddingHorizontal={'$4'}
    pressStyle={pressStyle}
    {...(Platform.OS === 'web' ? { cursor: 'pointer' } : undefined)}
  >
    <LabelL color={'$mono900'} fontWeight={'$2'}>
      {'Archived Chats'}
    </LabelL>
    <Icon color={'$mono500'} iconName={'chevronRightIcon'} size={'$4'} />
  </XStack>
);

const getChannelIcon = (kind: ChatInboxListRow['kind']) => {
  if (kind === 'updates') {
    return (
      <ChannelIcon
        backgroundColor={'$mono300'}
        iconName={'trendingIcon'}
        testID={'customer-inbox-updates-icon'}
      />
    );
  }

  return (
    <ChannelIcon
      backgroundColor={'$brand500'}
      iconName={'southStreetIcon'}
      iconSize={'$4'}
      testID={'customer-inbox-support-icon'}
    />
  );
};

const ChatInboxList = ({
  rows,
  isDesktop,
  onPressArchived,
  onPressRow,
}: ChatInboxListProps) => (
  <YStack
    backgroundColor={isDesktop ? '$mono200' : '$mono100'}
    borderRightColor={isDesktop ? '$mono300' : undefined}
    borderRightWidth={isDesktop ? 1 : 0}
    flex={isDesktop ? undefined : 1}
    flexShrink={isDesktop ? 0 : undefined}
    overflow={'hidden'}
    width={isDesktop ? '31%' : undefined}
  >
    <YStack
      backgroundColor={'$mono100'}
      borderBottomColor={isDesktop ? '$mono300' : undefined}
      borderBottomWidth={isDesktop ? 1 : 0}
    >
      <HeadingL
        color={'$mono900'}
        paddingHorizontal={'$4'}
        paddingTop={'$4'}
        paddingBottom={'$2'}
      >
        {'Inbox'}
      </HeadingL>
    </YStack>

    <ScrollView style={{ flex: 1 }}>
      {rows.map((row) => (
        <YStack backgroundColor={'$mono100'} key={row.id}>
          <ConversationRow
            avatar={getChannelIcon(row.kind)}
            onPress={() => onPressRow(row.kind)}
            selected={row.selected}
            selectedBackgroundColor={isDesktop ? '$brand100' : undefined}
            subtitle={row.subtitle}
            timestamp={row.timestamp}
            testID={`customer-inbox-row-${row.kind}`}
            title={row.title}
            unreadCount={row.unreadCount}
            unreadBadgeTestID={`customer-inbox-row-${row.kind}-unread-badge`}
          />
          <YStack borderBottomColor={'$mono300'} borderBottomWidth={1} />
        </YStack>
      ))}
    </ScrollView>

    <ArchivedChatsButton isDesktop={isDesktop} onPress={onPressArchived} />
  </YStack>
);

export { ChatInboxList };
export type { ChatInboxListProps };
